// src/lib/ownerCheckIn.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The pure half of the owner's weekly check-in on Today.
//
// GIVE THE OWNER SOMETHING TO DO. docs/IDEAS.md, 2026-08-27: every training
// platform makes the owner read only, and read only users churn. He logs in
// twice, sees a dashboard he did not ask for and never returns, and at renewal
// nobody in the building can say what it did.
//
// The act is small and it points downward: once a week, look at whether the
// process ran, and recognise the manager who ran it. Positive by default. The
// check-in never asks the owner to grade anybody, and nothing about it is
// shown to the manager except a mark, if the owner gives one.
//
// Voice rules: no Oxford commas. No em dashes. Real apostrophes.

import { addDays, parseDay } from './localDate.js'

// The Monday of the week a day falls in, as a day key. Dealership weeks are
// argued about; Monday is the one both the calendar and the weekly rhythm
// (manager_week.week_start) already use.
export function weekStartKey(today) {
  const d = parseDay(today)
  const back = (d.getDay() + 6) % 7 // Monday 0 ... Sunday 6
  return addDays(today, -back)
}

// Whether the owner has already checked in this week, from their own
// owner_checkin events. `null` events means the read failed, which is not the
// same as not having checked in: the card then shows itself rather than
// claiming it is done.
export function checkedInThisWeek(events, today) {
  if (!events) return null
  const week = weekStartKey(today)
  return events.find((e) => e.kind === 'owner_checkin' && e.metadata?.week === week) || false
}

// One line about recognition on the floor this week, as counts of people.
// Recognition of process behaviour is item six of Jazz's weekly rhythm, and
// the owner is the one person who can ask whether it is happening without it
// being a complaint.
export function recognitionLine(marks, floorIds) {
  if (!marks) return null
  const floor = new Set(floorIds || [])
  if (floor.size === 0) return null
  const noticed = new Set(marks.map((m) => m.given_to).filter((id) => floor.has(id))).size
  if (noticed === 0) return 'Nobody on the floor was given a mark this week.'
  if (noticed === floor.size) return `Everybody on the floor was recognised for something this week.`
  return `${noticed} of ${floor.size} people on the floor were recognised for something this week.`
}

// The four disciplines as one compact line for Today. The full rows live on
// the team page; this is the half-minute version.
export function compactWeek(rows) {
  const by = Object.fromEntries((rows || []).map((r) => [r.key, r]))
  if (!by.morning) return []
  return [
    { label: 'Meetings', value: `${by.morning.count}/${by.morning.of}` },
    { label: 'One on ones', value: `${by.oneonone.count}/${by.oneonone.of}` },
    { label: 'Inspections', value: `${by.audit.count}/${by.audit.of}` },
    { label: 'Sign offs', value: String(by.signoff.count) },
  ]
}
